import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute, useParams } from '@tanstack/react-router';
import { FileIcon } from 'lucide-react';
import { useState, type JSX } from 'react';

import { Button } from '@/components/ui/button.js';
import { Skeleton } from '@/components/ui/skeleton.js';
import {
  formatBytes,
  typeLabel,
} from '@/features/artifacts/artifact-display.js';
import { useUiStore } from '@/features/settings/ui-store.js';
import { resolveDefaultWorkspaceId } from '@/features/workspace/workspace-access.js';
import {
  artifactShareContentUrl,
  claimArtifactShare,
  fetchArtifactShareCard,
  sharedWithMeKeys,
  type ArtifactShareCard,
} from '@/lib/api/artifact-shares.js';
import { workspacesQueryOptions } from '@/lib/api/workspaces.js';
import { navigateWithPageLoad } from '@/lib/page-navigation.js';
import { signInWithSso } from '@/lib/sso.js';

export const Route = createFileRoute('/shared/$shareId')({
  component: SharedArtifactPage,
});

/** The date the file was shared, in words rather than a timestamp. */
function formatSharedAt(iso: string): string {
  const parsed = new Date(iso);
  if (Number.isNaN(parsed.getTime())) return '';
  return parsed.toLocaleDateString(undefined, {
    day: 'numeric',
    month: 'long',
  });
}

/** The one action a card offers, resolved from it once so the page renders a single button. */
type ShareAction =
  | { kind: 'claim' }
  | { kind: 'open-copy'; artifactId: string }
  | { kind: 'open-original'; artifactId: string; workspaceId: string }
  | { kind: 'download'; url: string }
  | { kind: 'none' };

/**
 * Which button the card earns.
 *
 * A copy already taken wins over the offer to take one: the service sets `can_claim` false once a caller
 * has claimed, and if the two ever disagreed, opening what they already have is the answer that cannot
 * make a second copy.
 *
 * The download is last of the real answers, so a reader who can take a copy is offered the copy and only
 * somebody with nothing to claim into gets the raw bytes — which is the anonymous reader of a `public`
 * share, the one case where the card comes back at all without a session. A card offering nothing falls
 * through to the unavailable screen.
 */
function shareAction(card: ArtifactShareCard): ShareAction {
  if (card.claimedArtifactId !== null) {
    return { kind: 'open-copy', artifactId: card.claimedArtifactId };
  }
  if (card.originalArtifactId !== null && card.originalWorkspaceId !== null) {
    return {
      kind: 'open-original',
      artifactId: card.originalArtifactId,
      workspaceId: card.originalWorkspaceId,
    };
  }
  if (card.canClaim) return { kind: 'claim' };
  if (card.accessMode === 'public') {
    return { kind: 'download', url: artifactShareContentUrl(card.id) };
  }
  return { kind: 'none' };
}

/**
 * `/shared/:shareId` — what somebody sees when a colleague sends them a file.
 *
 * Renders with no session. `isPublicViewPath` lifts this path out of `AuthGate`, `TeamsOnlyGate` and
 * `MaintenanceGate` and past the identity + Teams bootstrap, so nothing here may assume an identity, a
 * workspace or a bearer: the card read sends a token only if one already exists, and the authenticated
 * workspaces query runs only for the one button that needs it.
 *
 * A 401 is the invitation to sign in, never the unavailable screen — that screen is the single answer to
 * missing, revoked, expired and not-addressed-to-you, which stay indistinguishable so that none of them
 * confirms the file exists.
 *
 * The workspace the file came from is never named. Its id travels in the card only when the reader is
 * already a member of it, and then only so the button can open the original.
 *
 * Taking the copy also drops the cached "Shared with me" list, because the share has stopped waiting.
 */
function SharedArtifactPage(): JSX.Element {
  const { shareId } = useParams({ from: '/shared/$shareId' });

  const cardQuery = useQuery({
    queryKey: ['artifact-share-card', shareId],
    queryFn: () => fetchArtifactShareCard(shareId),
    retry: false,
    staleTime: 0,
    gcTime: 0,
  });
  const result = cardQuery.data ?? null;
  const card = result !== null && result.kind === 'card' ? result.card : null;
  const action = card === null ? { kind: 'none' as const } : shareAction(card);

  /**
   * The authenticated workspaces call is made only for the one button that needs it — opening a copy the
   * reader already took, which lives in their personal workspace and nowhere named on the card. Firing it
   * for anybody else would put a 401 in front of an anonymous reader, and the shared fetch wrapper
   * answers a 401 with an Entra renewal nobody asked for.
   */
  const workspacesQuery = useQuery({
    ...workspacesQueryOptions(),
    enabled: action.kind === 'open-copy',
  });
  const queryClient = useQueryClient();
  const claim = useMutation({
    mutationFn: () => claimArtifactShare(shareId),
    onSuccess: (claimed) => {
      /**
       * The share has stopped waiting for anybody: it is an ordinary file in the reader's artifacts now.
       * Dropping the cached "Shared with me" list is what takes the row out at once — without it the file
       * would sit there, still offering to be saved, until something else happened to refetch.
       */
      void queryClient.invalidateQueries({ queryKey: sharedWithMeKeys.list() });
      openArtifact(claimed.workspaceId, claimed.artifactId);
    },
  });

  /**
   * Show one file in the artifacts section. The section resolves its workspace from the store, so the
   * selection is switched FIRST — navigating alone opens whichever workspace the reader was in and finds
   * nothing there. This page also booted through the public-view bypass, so leaving it requires a full page
   * load: an in-app route change would keep the authenticated app's bootstrap side effects skipped.
   *
   * The reason is `'system'`: the reader asked for a file, not for a different workspace. `'user'` is the
   * switcher's own reason and it clears the open chat session, which nobody taking a copy asked for.
   */
  function openArtifact(workspaceId: string, artifactId: string): void {
    useUiStore.getState().setSelectedWorkspaceId(workspaceId, 'system');
    navigateWithPageLoad(`/artifacts?file=${encodeURIComponent(artifactId)}`);
  }

  /**
   * The copy lands in the reader's personal workspace, which is the one the switcher defaults to. Null
   * until the list arrives, which is why the button that uses it waits for the list.
   */
  const personalWorkspaceId = resolveDefaultWorkspaceId(
    workspacesQuery.data ?? [],
    null,
  );

  if (cardQuery.isLoading) {
    return (
      <PageShell>
        <div className="flex w-full max-w-sm flex-col gap-2" aria-busy>
          <Skeleton className="h-6 w-2/3 rounded-md" />
          <Skeleton className="h-4 w-1/2 rounded-md" />
        </div>
      </PageShell>
    );
  }

  if (result !== null && result.kind === 'needs-auth') {
    return <SignInScreen onSignedIn={() => void cardQuery.refetch()} />;
  }

  if (card === null || action.kind === 'none') {
    return (
      <PageShell>
        <p className="text-sm text-muted-foreground">
          This link is unavailable.
        </p>
      </PageShell>
    );
  }

  return (
    <PageShell>
      <div className="flex w-full max-w-md flex-col items-center gap-4 rounded-xl border border-border bg-card p-6 text-center">
        <span className="flex size-12 items-center justify-center rounded-2xl bg-primary/12 text-primary">
          <FileIcon className="size-6" strokeWidth={1.75} />
        </span>
        <div className="flex flex-col gap-1">
          <p className="font-heading text-base font-semibold text-foreground">
            {card.fileName}
          </p>
          <p className="text-xs text-muted-foreground">
            {typeLabel(card.contentType, card.fileName)} ·{' '}
            {formatBytes(card.size)}
          </p>
          {card.sharedByName !== null && card.sharedAt !== null && (
            <p className="text-sm text-muted-foreground">
              {`${card.sharedByName} shared this on ${formatSharedAt(card.sharedAt)}`}
            </p>
          )}
        </div>

        {action.kind === 'claim' && (
          <>
            <p className="text-xs text-muted-foreground">
              You get your own copy. Changes you make to it are yours alone.
            </p>
            <Button
              variant="brand"
              disabled={claim.isPending}
              onClick={() => claim.mutate()}
            >
              {claim.isPending ? 'Saving…' : 'Save to my files'}
            </Button>
          </>
        )}

        {action.kind === 'open-copy' && (
          <Button
            variant="brand"
            disabled={personalWorkspaceId === null}
            onClick={() => {
              if (personalWorkspaceId !== null) {
                openArtifact(personalWorkspaceId, action.artifactId);
              }
            }}
          >
            Open my copy
          </Button>
        )}

        {action.kind === 'open-original' && (
          <>
            <p className="text-xs text-muted-foreground">
              You can already open this file, so there is nothing to copy.
            </p>
            <Button
              variant="brand"
              onClick={() =>
                openArtifact(action.workspaceId, action.artifactId)
              }
            >
              Open the file
            </Button>
          </>
        )}

        {action.kind === 'download' && (
          <Button variant="brand" asChild>
            <a href={action.url} rel="noreferrer">
              Download
            </a>
          </Button>
        )}
      </div>
    </PageShell>
  );
}

/** The centred frame every state of this page renders into. */
function PageShell({ children }: { children: JSX.Element }): JSX.Element {
  return (
    <div className="flex h-full flex-col items-center justify-center gap-6 px-6 py-20">
      {children}
    </div>
  );
}

/**
 * The 401 screen. The link is fine and the reader is not signed in, so the answer is a way in — the same
 * one the chat-snapshot viewer offers, down to the wording, because it is the same moment in the same
 * product. Nothing here says whether the file exists.
 */
function SignInScreen({ onSignedIn }: { onSignedIn: () => void }): JSX.Element {
  const [signingIn, setSigningIn] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function onSignIn(): Promise<void> {
    if (signingIn) return;
    setSigningIn(true);
    setError(null);
    try {
      await signInWithSso();
      onSignedIn();
    } catch {
      setError('Sign-in failed. Try again.');
    } finally {
      setSigningIn(false);
    }
  }

  return (
    <PageShell>
      <div className="flex flex-col items-center gap-4 text-center">
        <p className="max-w-sm text-sm text-muted-foreground">
          Sign in to see this file.
        </p>
        <Button
          variant="brand"
          disabled={signingIn}
          onClick={() => void onSignIn()}
        >
          {signingIn ? 'Signing in…' : 'Sign in with Microsoft to view'}
        </Button>
        {error !== null && (
          <p role="alert" className="text-sm text-destructive">
            {error}
          </p>
        )}
      </div>
    </PageShell>
  );
}
